import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Smile, Grid3X3, Zap, Wrench, Settings, Shield, Moon, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiRequest } from '../lib/utils';
import AppointmentModal from '../components/AppointmentModal';

const SERVICE_ICONS = {
  'Full Dentures': Smile,
  'Partial Dentures': Grid3X3,
  'Immediate Dentures': Zap,
  'Denture Repairs': Wrench,
  'Relines & Adjustments': Settings,
  'Custom Mouth Guards': Shield,
  'Sleep Appliances': Moon,
};

const SERVICE_DETAILS = {
  'Full Dentures': {
    features: ['Complete tooth replacement', 'Natural aesthetics', 'Comfortable fit', 'Durable materials'],
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600'
  },
  'Partial Dentures': {
    features: ['Preserve remaining teeth', 'Restore chewing function', 'Custom fit', 'Multiple options'],
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/6vveon8z_ChatGPT%20Image%20Feb%2026%2C%202026%2C%2012_35_20%20PM.png'
  },
  'Immediate Dentures': {
    features: ['Same-day placement', 'No gap in appearance', 'Healing support', 'Transition dentures'],
    image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600'
  },
  'Denture Repairs': {
    features: ['Fast turnaround', 'Crack repairs', 'Break fixes', 'Base repairs'],
    image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600'
  },
  'Relines & Adjustments': {
    features: ['Improve fit', 'Restore comfort', 'Extend lifespan', 'Regular maintenance'],
    image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=600'
  },
  'Custom Mouth Guards': {
    features: ['Teeth grinding protection', 'TMJ relief', 'Sports protection', 'Custom fit'],
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/jke1tdrf_custom%20mouthguard.jpg'
  },
  'Sleep Appliances': {
    features: ['Reduce snoring', 'Improve airway', 'Better sleep', 'Non-invasive'],
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/lct6u1ol_ChatGPT%20Image%20Feb%2026%2C%202026%2C%2001_04_42%20PM.png'
  },
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [showAppointment, setShowAppointment] = useState(false);

  useEffect(() => {
    apiRequest('/public/services').then(setServices).catch(console.error);
  }, []);

  return (
    <div className="pt-24 lg:pt-32" data-testid="services-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Services
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive denture and oral appliance services delivered with precision and care by licensed professionals.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, idx) => {
              const Icon = SERVICE_ICONS[service.title] || Smile;
              const details = SERVICE_DETAILS[service.title] || { features: [], image: '' };
              const isReversed = idx % 2 === 1;
              
              return (
                <div
                  key={service.id}
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}
                  data-testid={`service-detail-${service.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className={isReversed ? 'lg:order-2' : ''}>
                    <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-orange-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {service.title}
                    </h2>
                    <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="mt-8 space-y-3">
                      {details.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => setShowAppointment(true)}
                      className="mt-8 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6"
                    >
                      Schedule Consultation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  <div className={`relative ${isReversed ? 'lg:order-1' : ''}`}>
                    <div className={`absolute inset-0 rounded-3xl transform ${isReversed ? 'rotate-3 bg-slate-100' : '-rotate-3 bg-orange-100'}`} />
                    <div className={`relative rounded-3xl overflow-hidden shadow-lg ${service.title === 'Sleep Appliances' ? 'aspect-square bg-slate-100' : 'aspect-[4/3] bg-slate-200'}`}>
                      <img
                        src={details.image}
                        alt={service.title}
                        className={`w-full h-full ${service.title === 'Sleep Appliances' ? 'object-contain' : 'object-cover'}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-slate-900" data-testid="notice-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800 rounded-2xl p-8 lg:p-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-600/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Important Information
                </h3>
                <p className="mt-3 text-slate-300 leading-relaxed">
                  As a Denturist practice, we specialize exclusively in removable prosthodontics and oral appliances. We <strong className="text-white">do not</strong> provide:
                </p>
                <ul className="mt-4 space-y-2 text-slate-400">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                    Tooth extractions (must be performed by your dentist)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                    Dental implants or implant surgery
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                    Oral surgery
                  </li>
                </ul>
                <p className="mt-4 text-slate-400 text-sm">
                  If extractions are required, your dentist performs them, and we coordinate denture fabrication timing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600" data-testid="services-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Get Started?
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Schedule your consultation today and take the first step toward restoring your smile.
          </p>
          <Button
            onClick={() => setShowAppointment(true)}
            className="mt-10 bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all hover:scale-105"
            data-testid="services-schedule-btn"
          >
            Schedule Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
